import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react"

const SidebarContext = createContext<{
  collapsed: boolean
  toggle: () => void
  expand: () => void
  collapse: () => void
  clearTimers: () => void
  isTemporarilyExpanded: boolean
  isInitialized: boolean
}>({ 
  collapsed: false, 
  toggle: () => {}, 
  expand: () => {},
  collapse: () => {},
  clearTimers: () => {},
  isTemporarilyExpanded: false,
  isInitialized: false
})

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(true) // Always start collapsed
  const [isTemporarilyExpanded, setIsTemporarilyExpanded] = useState(false)
  const [autoCollapseTimer, setAutoCollapseTimer] = useState<NodeJS.Timeout | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  
  // Use ref to access current collapsed state without causing re-renders
  const collapsedRef = useRef(collapsed)
  
  // Update ref when collapsed state changes
  useEffect(() => {
    collapsedRef.current = collapsed
  }, [collapsed])
  
  // Initialize sidebar state on mount - always collapsed
  useEffect(() => {
    setIsInitialized(true)
  }, [])
  
  // Clear all active timers
  const clearTimers = useCallback(() => {
    if (autoCollapseTimer) {
      clearTimeout(autoCollapseTimer)
      setAutoCollapseTimer(null)
    }
  }, [autoCollapseTimer])
  
  // Manual toggle - no persistence, just toggle current state
  const toggle = useCallback(() => {
    clearTimers()
    setIsTemporarilyExpanded(false)
    setCollapsed((prev) => !prev)
  }, [clearTimers])
  
  // Expand sidebar (for hover/temporary expansion)
  const expand = useCallback(() => {
    clearTimers()
    if (collapsed) {
      setCollapsed(false)
      setIsTemporarilyExpanded(true)
    }
  }, [collapsed, clearTimers])
  
  // Collapse with optional delay
  const collapse = useCallback((delay: number = 0) => {
    clearTimers()
    
    if (delay > 0) {
      const timer = setTimeout(() => {
        setCollapsed(true)
        setIsTemporarilyExpanded(false)
        setAutoCollapseTimer(null)
      }, delay)
      setAutoCollapseTimer(timer)
    } else {
      setCollapsed(true)
      setIsTemporarilyExpanded(false)
    }
  }, [clearTimers])
  
  // Handle window resize to adjust sidebar on screen size changes
  useEffect(() => {
    const handleResize = () => {
      // Auto-collapse on mobile
      if (window.innerWidth < 768) {
        if (!collapsedRef.current) {
          setCollapsed(true)
          setIsTemporarilyExpanded(false)
        }
      }
    }
    
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, []) // Remove collapsed dependency to prevent infinite loop
  
  // Clean up all timers on unmount
  useEffect(() => {
    return () => {
      clearTimers()
    }
  }, [clearTimers])

  return (
    <SidebarContext.Provider value={{ 
      collapsed, 
      toggle, 
      expand, 
      collapse, 
      clearTimers,
      isTemporarilyExpanded,
      isInitialized
    }}>
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  return useContext(SidebarContext)
}